import React, { useState, useRef } from 'react';
import {
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import { Vehiculo } from '../../services/vehiculosService';

interface Documento {
  id: string;
  nombre: string;
  tipo: 'soat' | 'tecnicomecanica' | 'tarjeta_propiedad' | 'licencia_conduccion' | 'poliza' | 'otro';
  url: string;
  fechaSubida: string;
  fechaVencimiento?: string;
  tamaño: number; // en bytes
  estado: 'vigente' | 'por_vencer' | 'vencido';
}

interface ModalDocumentosVehiculoProps {
  isOpen: boolean;
  onClose: () => void;
  vehiculo: Vehiculo | null;
}

const ModalDocumentosVehiculo: React.FC<ModalDocumentosVehiculoProps> = ({
  isOpen,
  onClose,
  vehiculo
}) => {
  // Estados
  const [documentos, setDocumentos] = useState<Documento[]>([
    {
      id: '1',
      nombre: 'SOAT_ABC123_2024.pdf',
      tipo: 'soat',
      url: 'https://dropbox.com/s/abc123/soat.pdf',
      fechaSubida: '2024-01-15T10:30:00Z',
      fechaVencimiento: '2024-12-31',
      tamaño: 2048576, // 2MB
      estado: 'vigente'
    },
    {
      id: '2',
      nombre: 'TecnicoMecanica_ABC123_2024.pdf',
      tipo: 'tecnicomecanica',
      url: 'https://dropbox.com/s/def456/tecnicomecanica.pdf',
      fechaSubida: '2024-01-10T08:15:00Z',
      fechaVencimiento: '2024-06-30',
      tamaño: 1536000, // 1.5MB
      estado: 'por_vencer'
    },
    {
      id: '3',
      nombre: 'TarjetaPropiedad_ABC123.pdf',
      tipo: 'tarjeta_propiedad',
      url: 'https://dropbox.com/s/ghi789/tarjeta_propiedad.pdf',
      fechaSubida: '2024-01-05T12:00:00Z',
      tamaño: 1024000, // 1MB
      estado: 'vigente'
    }
  ]);

  const [cargandoArchivo, setCargandoArchivo] = useState(false);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState<File | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState<string>('otro');
  const [fechaVencimiento, setFechaVencimiento] = useState<string>('');
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);

  const inputFileRef = useRef<HTMLInputElement>(null);

  // Funciones de utilidad
  const formatearTamaño = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatearFecha = (fecha: string): string => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'soat':
      case 'tecnicomecanica':
      case 'tarjeta_propiedad':
      case 'licencia_conduccion':
      case 'poliza':
        return <DocumentTextIcon className="h-8 w-8 text-blue-500" />;
      default:
        return <DocumentTextIcon className="h-8 w-8 text-gray-500" />;
    }
  };

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'vigente':
        return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'por_vencer':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400';
      case 'vencido':
        return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const obtenerNombreTipo = (tipo: string): string => {
    const tipos: { [key: string]: string } = {
      soat: 'SOAT',
      tecnicomecanica: 'Técnico Mecánica',
      tarjeta_propiedad: 'Tarjeta de Propiedad',
      licencia_conduccion: 'Licencia de Conducción',
      poliza: 'Póliza de Seguro',
      otro: 'Otro'
    };
    return tipos[tipo] || 'Documento';
  };

  // Funciones de manejo de archivos
  const manejarSeleccionArchivo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = event.target.files?.[0];
    if (archivo) {
      // Validar tipo de archivo
      const tiposPermitidos = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!tiposPermitidos.includes(archivo.type)) {
        setError('Solo se permiten archivos PDF, JPG, JPEG y PNG');
        return;
      }

      // Validar tamaño (máximo 10MB)
      if (archivo.size > 10 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 10MB');
        return;
      }

      setArchivoSeleccionado(archivo);
      setError(null);
    }
  };

  const subirArchivo = async () => {
    if (!archivoSeleccionado || !vehiculo) return;

    setCargandoArchivo(true);
    setError(null);

    try {
      // Simulación de subida a Dropbox
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Crear nuevo documento
      const nuevoDocumento: Documento = {
        id: Date.now().toString(),
        nombre: archivoSeleccionado.name,
        tipo: tipoDocumento as any,
        url: `https://dropbox.com/s/${Date.now()}/${archivoSeleccionado.name}`,
        fechaSubida: new Date().toISOString(),
        fechaVencimiento: fechaVencimiento || undefined,
        tamaño: archivoSeleccionado.size,
        estado: fechaVencimiento ? 
          (new Date(fechaVencimiento) > new Date() ? 'vigente' : 'vencido') : 
          'vigente'
      };

      setDocumentos(prev => [...prev, nuevoDocumento]);
      setExito('Documento subido exitosamente');
      
      // Limpiar formulario
      setArchivoSeleccionado(null);
      setTipoDocumento('otro');
      setFechaVencimiento('');
      setMostrarFormulario(false);
      
      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }

    } catch (error) {
      setError('Error al subir el archivo. Intenta nuevamente.');
      console.error('Error:', error);
    } finally {
      setCargandoArchivo(false);
    }
  };

  const eliminarDocumento = async (documentoId: string) => {
    try {
      // Simulación de eliminación en Dropbox
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDocumentos(prev => prev.filter(doc => doc.id !== documentoId));
      setExito('Documento eliminado exitosamente');
    } catch (error) {
      setError('Error al eliminar el documento');
      console.error('Error:', error);
    }
  };

  const descargarDocumento = (documento: Documento) => {
    // Simulación de descarga desde Dropbox
    window.open(documento.url, '_blank');
  };

  const previsualizarDocumento = (documento: Documento) => {
    // Abrir en nueva ventana para previsualización
    window.open(documento.url, '_blank');
  };

  // Limpiar mensajes después de 3 segundos
  React.useEffect(() => {
    if (error || exito) {
      const timer = setTimeout(() => {
        setError(null);
        setExito(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, exito]);

  if (!vehiculo) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" title="Documentos del Vehículo">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FolderIcon className="h-6 w-6 mr-2 text-blue-600" />
              Documentos del Vehículo
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {vehiculo.marca} {vehiculo.modelo} - {vehiculo.placa}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar modal de documentos del vehículo"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div role="alert" className="mb-4 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        )}

        {exito && (
          <div role="status" className="mb-4 p-4 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-green-700 dark:text-green-300">{exito}</span>
          </div>
        )}

        {/* Botón para agregar documento */}
        <div className="mb-6">
          <button
            onClick={() => setMostrarFormulario(!mostrarFormulario)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <CloudArrowUpIcon className="h-5 w-5 mr-2" />
            Subir Documento
          </button>
        </div>

        {/* Formulario de subida */}
        {mostrarFormulario && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Subir Nuevo Documento
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="vehiculo-documento-tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Documento
                </label>
                <select
                  id="vehiculo-documento-tipo"
                  name="tipoDocumento"
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                >
                  <option value="soat">SOAT</option>
                  <option value="tecnicomecanica">Técnico Mecánica</option>
                  <option value="tarjeta_propiedad">Tarjeta de Propiedad</option>
                  <option value="licencia_conduccion">Licencia de Conducción</option>
                  <option value="poliza">Póliza de Seguro</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="vehiculo-documento-vencimiento" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Vencimiento (Opcional)
                </label>
                <input
                  id="vehiculo-documento-vencimiento"
                  name="fechaVencimiento"
                  type="date"
                  value={fechaVencimiento}
                  onChange={(e) => setFechaVencimiento(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="vehiculo-documento-archivo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Seleccionar Archivo
              </label>
              <input
                id="vehiculo-documento-archivo"
                name="archivo"
                ref={inputFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={manejarSeleccionArchivo}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Formatos permitidos: PDF, JPG, JPEG, PNG. Tamaño máximo: 10MB
              </p>
            </div>

            {archivoSeleccionado && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Archivo seleccionado: {archivoSeleccionado.name} ({formatearTamaño(archivoSeleccionado.size)})
                </p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={subirArchivo}
                disabled={!archivoSeleccionado || cargandoArchivo}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
              >
                {cargandoArchivo ? (
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                )}
                {cargandoArchivo ? 'Subiendo...' : 'Subir'}
              </button>
              
              <button
                onClick={() => {
                  setMostrarFormulario(false);
                  setArchivoSeleccionado(null);
                  setTipoDocumento('otro');
                  setFechaVencimiento('');
                  if (inputFileRef.current) {
                    inputFileRef.current.value = '';
                  }
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Lista de documentos */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Documentos Almacenados ({documentos.length})
          </h3>

          {documentos.length === 0 ? (
            <div className="text-center py-8">
              <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No hay documentos almacenados para este vehículo
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {documentos.map((documento) => (
                <div
                  key={documento.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {obtenerIconoTipo(documento.tipo)}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                          {documento.nombre}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {obtenerNombreTipo(documento.tipo)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatearTamaño(documento.tamaño)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Subido: {formatearFecha(documento.fechaSubida)}
                          </span>
                        </div>
                        {documento.fechaVencimiento && (
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${obtenerColorEstado(documento.estado)}`}>
                              Vence: {formatearFecha(documento.fechaVencimiento)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => previsualizarDocumento(documento)}
                        aria-label={`Previsualizar ${documento.nombre}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Previsualizar"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => descargarDocumento(documento)}
                        aria-label={`Descargar ${documento.nombre}`}
                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                        title="Descargar"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => eliminarDocumento(documento.id)}
                        aria-label={`Eliminar ${documento.nombre}`}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Almacenamiento: Dropbox Business
            </div>
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalDocumentosVehiculo;
